import { useFlowMessage } from "@/api/messages";
import { useOnScreenEditorsContext } from "@/context/OnScreenEditorsContext";
import analytics from "@/utils/analytics";
import {
  ConnectionPayload,
  Entity,
  EntityCard,
  getEntityData,
  getEntityName,
  getEntityType,
  INSERT_CONNECTION_COMMAND,
} from "@cohesive/ui-components";
import { useMemo } from "react";
import { AnimatedListItem, DialogListContainer } from ".";

const UNSUPPORTED_ENTITIES = ["notion_block"];

const getEntityId = (entity: Entity) => {
  const entityData = getEntityData(entity);

  if (entityData && "id" in entityData) {
    return entityData.id;
  }

  return null;
};

interface EntitiesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  flowSessionId: string;
  flowMessageId: string;
}

export function EntitiesDialog({
  open,
  onOpenChange,
  flowSessionId,
  flowMessageId,
}: EntitiesDialogProps) {
  const { onScreenEditors } = useOnScreenEditorsContext();

  const { data: message } = useFlowMessage(flowSessionId, flowMessageId);
  const entities = useMemo(
    () =>
      (message?.result?.entities || [])
        ?.filter((e) => "type" in e)
        ?.filter((e) => !UNSUPPORTED_ENTITIES.includes(e.type))
        ?.filter(getEntityData),
    [message?.result?.entities],
  );

  const addMention = (entity: Entity) => {
    const sideEditor = onScreenEditors.find(
      (editor) => editor.location === "sidebar",
    );

    const mainEditor = onScreenEditors.find(
      (editor) => editor.location === "main",
    );

    const editorToUse = sideEditor || mainEditor;

    const entityData = getEntityData(entity);

    if (!entityData) {
      return;
    }

    const e = {
      // Generic entities fix `platform` to "generic" and carry the real
      // source platform in `data.platform_name` — prefer that so the mention
      // still identifies e.g. Zoom/Gmail rather than just "generic".
      platform: entityData?.platform_name || entity.platform,
      type: getEntityType(entity),
      identifier: entityData.id?.toString().replaceAll(":", "%3A"),
      title: getEntityName(entity),
    } as ConnectionPayload;

    editorToUse?.editor.dispatchCommand(INSERT_CONNECTION_COMMAND, e);

    analytics.trackIntegrationConnectionRecordSelected({
      entity: {
        platform: e.platform,
        type: e.type,
        id: e.identifier,
        name: e.title,
      },
    });

    // Close the dialog after adding mention
    onOpenChange(false);
  };

  if (!entities?.length) {
    return null;
  }

  return (
    <DialogListContainer
      open={open}
      onOpenChange={onOpenChange}
      title="Sources"
      isEmpty={!entities?.length}
    >
      {entities.map((entity, i) => {
        const entityData = getEntityData(entity);
        const entityId = getEntityId(entity);
        if (!entityData) {
          return null;
        }

        return (
          <AnimatedListItem
            key={entityId || i}
            itemKey={entityId || i}
            index={i}
          >
            <EntityCard
              entity={entity}
              onClick={() => {
                addMention(entity);
              }}
              className="rounded-none shadow-none border-none"
            />
          </AnimatedListItem>
        );
      })}
    </DialogListContainer>
  );
}
